import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { alpha, darken, lighten } from '@mui/material/styles';
import { keys } from 'essential-utils';
import { observer } from 'mobx-react';
import { useEffect, useRef, useState } from 'react';
import { Alert, CircularProgress, FlexBox, styled } from 'ml-rubiks-styleguide';
import { injectTSDI } from 'ml-webclient-common/src/tsdi';
import { Config } from '../../core/config';
import { I18nStore } from '../../core/i18n';
import { loadScript } from '../../core/load-script';
import { TEXT_PLACEHOLDER_ALPHA, TEXT_SECONDARY_ALPHA, TONAL_OFFSET_DARK, TONAL_OFFSET_LIGHT } from '../../core/styling-constants';
import { FastFinanceLinkStore } from '../../store';
export const hasPaymentRedirectParams = () => {
    const { searchParams } = new URL(window.location.href);
    return searchParams.has('paymentToken');
};
function buildWidgetStyling(styling) {
    const stripeVariables = {
        borderRadius: '8px'
    };
    if (styling?.highlightColor) {
        stripeVariables.colorPrimary = styling.highlightColor;
    }
    if (styling?.backgroundColor) {
        stripeVariables.colorBackground = styling.backgroundColor;
    }
    if (styling?.textColor) {
        stripeVariables.colorText = styling.textColor;
    }
    if (styling?.errorColor) {
        stripeVariables.colorDanger = styling.errorColor;
    }
    const textColorSecondary = styling?.textColor
        ? alpha(styling.textColor, TEXT_SECONDARY_ALPHA)
        : undefined;
    if (textColorSecondary) {
        stripeVariables.colorTextSecondary = textColorSecondary;
    }
    const stripeRules = {};
    if (styling?.hrColor) {
        stripeRules['.Input'] = {
            border: `1px solid ${styling.hrColor}`
        };
        stripeRules['.Tab'] = {
            border: `1px solid ${styling.hrColor}`
        };
    }
    const adyenBase = {};
    if (styling?.textColor) {
        adyenBase.color = styling.textColor;
    }
    if (styling?.backgroundColor2) {
        adyenBase.background = styling.backgroundColor2;
    }
    adyenBase.fontSize = '16px';
    const adyenError = {};
    if (styling?.errorColor) {
        adyenError.color = styling.errorColor;
    }
    const adyenPlaceholder = {};
    if (styling?.textColor) {
        adyenPlaceholder.color = alpha(styling.textColor, TEXT_PLACEHOLDER_ALPHA);
    }
    return {
        primaryColor: styling?.highlightColor ?? '#1976d2',
        textColorMain: styling?.textColor,
        textColorSecondary,
        borderColor: styling?.hrColor,
        borderRadius: '8px',
        backgroundColor: 'transparent',
        stripe: {
            appearance: {
                theme: 'stripe',
                variables: stripeVariables,
                ...(keys(stripeRules).length > 0 && {
                    rules: stripeRules
                })
            }
        },
        adyen: {
            styles: {
                base: adyenBase,
                ...(keys(adyenError).length > 0 && {
                    error: adyenError
                }),
                ...(keys(adyenPlaceholder).length > 0 && {
                    placeholder: adyenPlaceholder
                })
            }
        }
    };
}
function buildAdyenCssVariables(styling) {
    if (!styling) {
        return {};
    }
    const vars = {};
    if (styling.backgroundColor2) {
        vars['--adyen-sdk-color-background-primary'] = styling.backgroundColor2;
    }
    if (styling.backgroundColor) {
        vars['--adyen-sdk-color-background-secondary'] =
            styling.backgroundColor;
    }
    if (styling.textColor) {
        vars['--adyen-sdk-color-label-primary'] = styling.textColor;
        vars['--adyen-sdk-color-label-secondary'] = alpha(styling.textColor, TEXT_SECONDARY_ALPHA);
    }
    if (styling.highlightColor) {
        vars['--adyen-sdk-color-label-highlight'] = styling.highlightColor;
        vars['--adyen-sdk-color-background-always-dark'] =
            styling.highlightColor;
        vars['--adyen-sdk-color-background-always-dark-active'] = darken(styling.highlightColor, TONAL_OFFSET_DARK);
        vars['--adyen-sdk-color-background-inverse-primary-hover'] = lighten(styling.highlightColor, TONAL_OFFSET_LIGHT);
    }
    if (styling.highlightTextColor) {
        vars['--adyen-sdk-color-label-on-color'] = styling.highlightTextColor;
    }
    if (styling.errorColor) {
        vars['--adyen-sdk-color-label-critical'] = styling.errorColor;
        vars['--adyen-sdk-color-outline-critical'] = styling.errorColor;
        vars['--adyen-sdk-color-background-critical-strong'] =
            styling.errorColor;
    }
    if (styling.hrColor) {
        vars['--adyen-sdk-color-outline-primary'] = styling.hrColor;
        vars['--adyen-sdk-color-outline-primary-active'] = styling.hrColor;
        vars['--adyen-sdk-color-separator-primary'] = styling.hrColor;
    }
    return vars;
}
const WidgetContainer = styled('div')(({ $styling, $visible }) => ({
    opacity: $visible ? 1 : 0,
    ...buildAdyenCssVariables($styling)
}));
export const PaymentWidgetWrapper = observer(function PaymentWidgetWrapper({ scope }) {
    const store = injectTSDI(FastFinanceLinkStore);
    const config = injectTSDI(Config);
    const i18n = injectTSDI(I18nStore);
    const containerRef = useRef(null);
    const widgetRef = useRef(null);
    const [isInitializing, setIsInitializing] = useState(true);
    const [error, setError] = useState(null);
    useEffect(() => {
        let mounted = true;
        const initWidget = async () => {
            try {
                if (!hasPaymentRedirectParams()) {
                    await store.createPaymentSession(scope);
                }
                if (!mounted ||
                    !containerRef.current ||
                    !store.userSessionToken) {
                    return;
                }
                await loadScript(config.paymentWidgetUrl);
                if (!mounted || !containerRef.current) {
                    return;
                }
                const paymentWidgetGlobal = window.paymentWidget;
                if (!paymentWidgetGlobal) {
                    throw new Error('Payment widget not loaded. Ensure widget.js is included.');
                }
                widgetRef.current = paymentWidgetGlobal.init({
                    userSessionToken: store.userSessionToken,
                    environment: config.paymentEnvironment,
                    region: store.finionPayRegion,
                    countryCode: store.studioInfo?.address?.country ?? 'DE',
                    locale: i18n.currentLocale.split('-')[0],
                    container: containerRef.current,
                    styling: buildWidgetStyling(store.styling),
                    featureFlags: {
                        useRubiksUI: true
                    },
                    onSuccess: async (paymentRequestToken) => {
                        try {
                            await store.handlePaymentSuccess(paymentRequestToken);
                        }
                        catch (err) {
                            console.error('Payment processing failed:', err);
                            setError(err instanceof Error
                                ? err.message
                                : 'Payment processing failed');
                        }
                    }
                });
                setIsInitializing(false);
            }
            catch (err) {
                console.error('Failed to initialize payment widget:', err);
                setError(err instanceof Error
                    ? err.message
                    : 'Failed to initialize payment');
                setIsInitializing(false);
            }
        };
        void initWidget();
        return () => {
            mounted = false;
            widgetRef.current?.destroy();
        };
    }, [store, config, i18n, scope]);
    if (error) {
        return _jsx(Alert, { severity: "error", children: error });
    }
    return (_jsxs(FlexBox, { stack: true, sx: { minHeight: 300, position: 'relative' }, children: [isInitializing && (_jsx(FlexBox, { hAlign: "center", vAlign: "center", sx: {
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0
                }, children: _jsx(CircularProgress, { size: 24 }) })), _jsx(WidgetContainer, { ref: containerRef, "$styling": store.styling, "$visible": !isInitializing })] }));
});
